import { formatDistanceToNow } from 'date-fns';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/components/ui/popover';
import { cn } from '@/utils/classUtils';
import type { AllDocumentsReferenceItem } from '@ditto/shared';
import { type FC, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReferenceUpdateSummaryCard } from '../components/ReferenceUpdateSummaryCard';
import { useLastUpdatedPreview } from './LastUpdatedPreviewContext';

/** Opening preview after hover avoids flashes when scrubbing across rows (see DITTO-1322 follow-up). */
const OPEN_DELAY_MS = 130;

export const LastUpdatedCell: FC<{
  ref_: AllDocumentsReferenceItem;
  columnWidth: number;
  className?: string;
  workspaceUname: string;
}> = ({ ref_, columnWidth, className, workspaceUname }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeReferenceId, setActiveReferenceId } = useLastUpdatedPreview();

  const refId = ref_.id;

  const [cellHovered, setCellHovered] = useState(false);
  const [contentHovered, setContentHovered] = useState(false);

  const tdRef = useRef<HTMLTableCellElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const cellHoveredRef = useRef(false);
  const openDelayTimerRef = useRef<number | null>(null);

  const clearOpenDelayTimer = () => {
    if (openDelayTimerRef.current !== null) {
      window.clearTimeout(openDelayTimerRef.current);
      openDelayTimerRef.current = null;
    }
  };

  useEffect(() => () => clearOpenDelayTimer(), []);

  const basePath = `/workspace/${workspaceUname}/regulatory-watch`;
  const preview = ref_.latestUpdate;

  const handleNavigateToUpdate = () => {
    if (!preview) return;
    navigate(`${basePath}/updates/${preview.id}`, {
      state: {
        backPath: `${location.pathname}${location.search}`,
        backLabel: 'My references',
      },
    });
  };

  const interactionActive = cellHovered || contentHovered;
  const popoverOpen =
    activeReferenceId === refId && (cellHovered || contentHovered);

  const inner = (
    <>
      {ref_.lastUpdatedAt ? (
        <div className="flex flex-col">
          <span className="font-mono text-[11px] leading-[16.5px] text-neutral-900 whitespace-nowrap">
            {ref_.lastUpdatedAt.substring(0, 10)}
          </span>
          <span className="text-[11px] leading-[16.5px] text-neutral-500 whitespace-nowrap">
            {formatDistanceToNow(new Date(ref_.lastUpdatedAt), {
              addSuffix: true,
            }).replace('about ', '')}
          </span>
        </div>
      ) : (
        <span className="font-mono text-[11px] leading-[16.5px] text-neutral-900">
          -
        </span>
      )}
    </>
  );

  const tdClass = cn(
    'px-2 py-2 align-middle overflow-hidden border-r border-[#e5e5e5]',
    className
  );

  if (!preview) {
    return (
      <td
        className={tdClass}
        style={{
          width: columnWidth,
          maxWidth: columnWidth,
        }}
      >
        {inner}
      </td>
    );
  }

  const dismissThisPreview = () => {
    clearOpenDelayTimer();
    setCellHovered(false);
    cellHoveredRef.current = false;
    setContentHovered(false);
    setActiveReferenceId(prev => (prev === refId ? null : prev));
  };

  return (
    <Popover
      modal={false}
      open={popoverOpen}
      onOpenChange={next => {
        if (!next) {
          dismissThisPreview();
        }
      }}
    >
      <PopoverAnchor asChild>
        <td
          ref={tdRef}
          className={tdClass}
          style={{
            width: columnWidth,
            maxWidth: columnWidth,
          }}
          onMouseEnter={() => {
            clearOpenDelayTimer();
            setCellHovered(true);
            cellHoveredRef.current = true;

            // Close any other row's preview immediately (single visible tooltip).
            setActiveReferenceId(prev =>
              prev !== null && prev !== refId ? null : prev
            );

            // Already showing this reference (e.g. pointer moved from popover back to cell).
            if (activeReferenceId === refId) {
              return;
            }

            openDelayTimerRef.current = window.setTimeout(() => {
              openDelayTimerRef.current = null;
              if (cellHoveredRef.current) {
                setActiveReferenceId(refId);
              }
            }, OPEN_DELAY_MS);
          }}
          onMouseLeave={e => {
            const next = e.relatedTarget as Node | null;
            if (contentRef.current?.contains(next)) {
              return;
            }
            clearOpenDelayTimer();
            setCellHovered(false);
            cellHoveredRef.current = false;
            setActiveReferenceId(prev => (prev === refId ? null : prev));
          }}
        >
          {inner}
        </td>
      </PopoverAnchor>
      <PopoverContent
        ref={contentRef}
        align="start"
        side="top"
        sideOffset={0}
        className="z-[60] w-full max-w-[min(306px,calc(100vw-24px))] border-0 bg-transparent p-0 shadow-none"
        onMouseEnter={() => {
          clearOpenDelayTimer();
          setContentHovered(true);
          setActiveReferenceId(refId);
        }}
        onMouseLeave={e => {
          const next = e.relatedTarget as Node | null;
          if (tdRef.current?.contains(next)) {
            setContentHovered(false);
            return;
          }
          setContentHovered(false);
          setActiveReferenceId(prev => (prev === refId ? null : prev));
        }}
        onOpenAutoFocus={e => e.preventDefault()}
        onCloseAutoFocus={e => e.preventDefault()}
        onClick={e => e.stopPropagation()}
      >
        <ReferenceUpdateSummaryCard
          update={preview}
          onClick={handleNavigateToUpdate}
          interactionActive={interactionActive}
          arrowEmphasized={contentHovered}
        />
      </PopoverContent>
    </Popover>
  );
};
